import { auth } from './auth.js';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface RequestOptions extends RequestInit {
  params?: Record<string, string>;
  expectedUserId?: string;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { params, expectedUserId, ...fetchOptions } = options;
    let url = `${this.baseUrl}${path}`;

    if (params) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }

    const accountAtStart =
      expectedUserId ?? (typeof auth.getUserId === 'function' ? auth.getUserId() : null);
    const tokens = await auth.getTokens();
    if (accountAtStart) {
      let subject: unknown;
      try {
        const payload = tokens?.idToken.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/');
        subject = payload ? (JSON.parse(atob(payload)) as { sub?: string }).sub : undefined;
      } catch {
        /* Invalid identity cannot authorize a queued write. */
      }
      if (subject !== accountAtStart)
        throw new ApiError(401, 'La cuenta cambió durante la sincronización');
    }

    const headers: Record<string, string> = {
      ...(fetchOptions.body ? { 'Content-Type': 'application/json' } : {}),
      ...(fetchOptions.headers as Record<string, string>),
    };

    if (tokens?.accessToken) {
      headers['Authorization'] = `Bearer ${tokens.accessToken}`;
    }

    const response = await fetch(url, { ...fetchOptions, headers });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new ApiError(response.status, error.message || 'Request failed');
    }

    const json = await response.json();
    // Unwrap API envelope { success: true, data: ..., meta: ... }
    if (json && typeof json === 'object' && 'success' in json && 'data' in json) {
      return json.data as T;
    }
    return json as T;
  }

  get<T>(path: string, params?: Record<string, string>) {
    return this.request<T>(path, { method: 'GET', params });
  }

  post<T>(path: string, body: unknown, expectedUserId?: string) {
    return this.request<T>(path, { method: 'POST', body: JSON.stringify(body), expectedUserId });
  }

  put<T>(path: string, body: unknown, expectedUserId?: string) {
    return this.request<T>(path, { method: 'PUT', body: JSON.stringify(body), expectedUserId });
  }

  patch<T>(path: string, body: unknown, expectedUserId?: string) {
    return this.request<T>(path, { method: 'PATCH', body: JSON.stringify(body), expectedUserId });
  }

  delete<T>(path: string, body?: unknown, expectedUserId?: string) {
    return this.request<T>(path, {
      method: 'DELETE',
      expectedUserId,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const api = new ApiClient(API_BASE);
